'use strict';


import {
    BoxGeometry,
    MeshPhongMaterial,
    Mesh,
    PlaneGeometry,
    DoubleSide,
    Vector2,
    Vector3,
    Box3,
    TextureLoader, MeshStandardMaterial, Color, LinearSRGBColorSpace, ClampToEdgeWrapping, Clock, Plane,
    RepeatWrapping,
    EquirectangularReflectionMapping
} from 'three';
import {
    World,
    DistanceConstraint,
    Vec3,
    Sphere,
    Body,
    Box
} from 'cannon-es';
import { assignDeep, Buildings, isMobile, settingsData, threeJsVariables } from '../../../settings';
import { getCSSVarValue } from '../../../functions';
import { addBuildingsIntersections, poiIntersection } from '../../poi/poiCss';
import { SelectedObjectType } from '../../../common';
import { updateShadowMap } from '../lights';
import { BmbyMode } from '../../mode/BmbyMode';


const DEFAULT_SETTINGS = {
    clothSegments: 26,
    coverAllBuildings: true,
    buildingsList: [],
    boxMarginTop: 0.10,
    boxMarginSides: 0.02,
    frontSideCoverage: 0.5,
    laterialSideCoverage: 0.25,
    logoMargin: 0.05,
    showBoundingBox: false
};


export class ClothSimulation {

    constructor() {
        this.params = assignDeep(DEFAULT_SETTINGS,config.features.cloth || {});
        this.active = (config.crmProjectSettings?.general?.status === 'delivery' || this.params?.active) && !isMobile && !BmbyMode.active;
        if (!this.active) return;
        this.speedUpCycles = 130;
        this.removed = false;
        this.animationEnabled = false;
        this.hovered = false;
        // vars
        this.clothParticles = [];
        this.constraints = [];
        this.clothMesh = null;
        this.clothGeometry = null;
        this.clothMaterial = null;
        this.clock = new Clock();
        this.delta = 0;
        this.elapsedTime = 0;
        this.color = null;
        // world vars
        this.world = new World();
        this.world.gravity.set(0, -110, 0);
        this.world.solver.iterations = 15;  // 10 - 20
        this.indexGrab = new Vector2();
        this.vectorGrab = new Vec3(0.15, -2, 0.15);
        // box vars
        this.boxSize = new Vector3();
        this.boxCenter = new Vector3();
        // cloth vars
        this.clothSegmentsX = null;
        this.clothSegmentsZ = null;
        this.particleRadius = 0.4;  // 0 - 0.5
        this.clothMass = 100;
        this.clothAngle = Math.PI / 180 * 0;
        this.clothLength = null;
        this.clothWidth = null;
        this.distance = null;
    }

    setup() {
        if (!this.active) return;
        this.#processData();
        this.#setupBox();
        this.#setupCloth();
        this.preSimulate();
        this.animate();
    }

    #processData() {
        const buildingsArray = Buildings.getBuildingsArray();
        const boundingBox = new Box3();
        buildingsArray.forEach(building => {
            if (!this.params.coverAllBuildings && !this.params.buildingsList.includes(building.name))
                return;

            const mesh = building.instances[0];
            mesh.updateMatrixWorld(true);
            const bBox = new Box3().setFromObject(mesh);
            boundingBox.union(bBox);
        });

        boundingBox.getSize(this.boxSize);
        boundingBox.getCenter(this.boxCenter);
        this.clothLength = this.boxSize.x + (this.boxCenter.y * 0.5 + this.boxSize.y) * this.params.laterialSideCoverage * 2;
        this.clothWidth = this.boxSize.z + (this.boxCenter.y * 0.5 + this.boxSize.y) * this.params.frontSideCoverage * 2;
        if (this.clothLength > this.clothWidth) {
            this.clothSegmentsX = this.params.clothSegments;
            this.clothSegmentsZ = Math.floor(this.clothSegmentsX * this.clothWidth / this.clothLength);
        }else {
            this.clothSegmentsZ = this.params.clothSegments;
            this.clothSegmentsX = Math.floor(this.clothSegmentsZ / this.clothWidth * this.clothLength);
        }
        this.indexGrab = new Vector2(0, this.clothSegmentsZ);
    }

    #setupBox() {
        if (this.params.showBoundingBox) {
            const boxGeometry = new BoxGeometry(this.boxSize.x * 1.01, this.boxSize.y * 1.01, this.boxSize.z * 1.01);
            const boxMaterial = new MeshPhongMaterial({
                color: 0xfa8e05,
                transparent: true,
                opacity: 0.6
            });
            const boxMesh = new Mesh(boxGeometry, boxMaterial);
            boxMesh.position.copy(this.boxCenter);
            threeJsVariables.scene.add(boxMesh);
            settingsData.objects_to_intersection.push(boxMesh);
        }

        const boxBody = new Body({
            mass: 0,
            position: new Vec3(0, 0, 0)
        });
        boxBody.addShape(new Box(new Vec3(
            this.boxSize.x * (0.5 + this.params.boxMarginSides),
            this.boxSize.y * (0.5),
            this.boxSize.z * (0.5 + this.params.boxMarginSides)
        )));
        this.world.addBody(boxBody);
    }

    #setupCloth() {
        this.clothGeometry = new PlaneGeometry(this.clothWidth, this.clothLength, this.clothSegmentsZ, this.clothSegmentsX);

        const loader =  new TextureLoader();
        const cloth = this;
        const logo = loader.load(config.general.logo, (texture) => {
            const aspectRatio = logo.image.width / logo.image.height;
            logo.wrapS = ClampToEdgeWrapping;
            logo.wrapT = ClampToEdgeWrapping;
            logo.rotation = -Math.PI / 2;

            const maxWidth = cloth.boxSize.x / cloth.clothLength;
            const maxHeight = (1 - cloth.boxSize.z / cloth.clothWidth) / 2;

            let width = maxWidth * (1 - this.params.logoMargin * 2);
            let height = maxHeight * (1 - this.params.logoMargin * 2);

            const newAspectRatio = width / height * cloth.clothLength / cloth.clothWidth;

            if (newAspectRatio > aspectRatio) {
                width *= aspectRatio / newAspectRatio;
            }else {
                height *= newAspectRatio / aspectRatio;
            }

            logo.repeat.set(1 / width, 1 / height);
            logo.offset.set(0.5 + 0.5 / width, 0.5 - 0.5 * maxHeight / height);
        });
        
        function updateShader (shader) {
            shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>',[
                'vec4 sampledDiffuseColor = texture2D( map, vMapUv );',
                'diffuseColor = mix(diffuseColor * 1.2,sampledDiffuseColor,sampledDiffuseColor.a);'
            ].join('\n'));
        }

        const aomap = loader.load('https://dreamsimages.bmby.com/DreamsGeneralStorage/textures/clothmaps/Velvet_Mask_Bump_1K.jpg');
        const lightmap = loader.load('https://dreamsimages.bmby.com/DreamsGeneralStorage/textures/clothmaps/Velvet_Mask_Bump_1K_con.jpg');
        const normalmap = loader.load('https://dreamsimages.bmby.com/DreamsGeneralStorage/textures/clothmaps/Untitled material_Normal.jpg');
        const roughnessmap = loader.load('https://dreamsimages.bmby.com/DreamsGeneralStorage/textures/clothmaps/ullnchdew_1K_Roughness.jpg');
        const envmap = loader.load('https://dreamsimages.bmby.com/DreamsGeneralStorage/textures/clothmaps/Flip_center 40m mini_v6.jpg');
        normalmap.wrapS = RepeatWrapping;
        normalmap.wrapT = RepeatWrapping;
        normalmap.repeat.set(1.5,1.5);
        envmap.mapping = EquirectangularReflectionMapping;

        const clothClippingPlane = new Plane(new Vector3(0, 1, 0), 0);
        this.clothMaterial = new MeshStandardMaterial({
            aoMap: aomap,
            aoMapIntensity: 0.2,
            lightMap: lightmap,
            lightMapIntensity: 0.3,
            normalMap: normalmap,
            normalScale: new Vector2(4,4),
            roughness: 1.00,
            roughnessMap: roughnessmap,
            side: DoubleSide,
            envMap: envmap,
            envMapIntensity: 1.0,
            map: logo,
            clippingPlanes: [clothClippingPlane],
            //color: new Color().setStyle('#9b2d30',LinearSRGBColorSpace),
            flatShading: true
        });
        if (this.color) this.clothMaterial.color = this.color;
        this.clothMaterial.customPrograms = [updateShader];
        this.clothMesh = new Mesh(this.clothGeometry, this.clothMaterial);
        this.clothMesh.scale.set(-1,1,1);
        this.clothMesh.position.set(this.boxCenter.x, this.boxCenter.y + this.boxSize.y * (0.01 + this.params.boxMarginTop), this.boxCenter.z);
        this.clothMesh.castShadow = true;
        this.clothMesh.receiveShadow = true;
        this.clothMesh.name = SelectedObjectType.Cloth;
        this.clothMesh.userData.type = SelectedObjectType.Cloth;
        threeJsVariables.scene.add(this.clothMesh);
        addBuildingsIntersections(this.clothMesh);
        settingsData.objects_to_intersection.push(this.clothMesh);

        this.#createClothPhysics();
    }

    updateColor() {
        if (!this.active) return;
        this.color = new Color().setStyle(getCSSVarValue('--menuBg'),LinearSRGBColorSpace);
        if (this.clothMaterial) this.clothMaterial.color = this.color;
    }

    #createClothPhysics() {
        this.distance = this.clothLength / this.clothSegmentsX;
        const particleRadius = this.distance * this.particleRadius;

        // create particles
        for (let i = 0; i <= this.clothSegmentsX; i++) {
            this.clothParticles.push([]);
            for (let j = 0; j <= this.clothSegmentsZ; j++) {
                // setup cloth rectangle coordinates
                let x = (i / this.clothSegmentsX - 0.5) * this.clothLength;
                let y = this.boxCenter.y + this.boxSize.y * (0.01 + this.params.boxMarginTop);
                let z = (j / this.clothSegmentsZ - 0.5) * this.clothWidth;
                // rotate particles
                // const newx = (x - this.boxCenter.x) * Math.cos(this.clothAngle) -
                //             (z - this.boxCenter.z) * Math.sin(this.clothAngle) +
                //             this.boxCenter.x;
                // const newz = (x - this.boxCenter.x) * Math.sin(this.clothAngle) +
                //             (z - this.boxCenter.z) * Math.cos(this.clothAngle) +
                //             this.boxCenter.z;
                // x = newx;
                // z = newz;
                const particle = new Body({
                    mass: this.clothMass / (this.clothSegmentsX * this.clothSegmentsZ),
                    position: new Vec3(x, y, z),
                    shape: new Sphere(particleRadius)
                });
                if (i == this.indexGrab.x && j == this.indexGrab.y) {
                    this.particleGrab = particle;
                }
                this.world.addBody(particle);
                this.clothParticles[i].push(particle);
            }
        }

        this.#createConstraints();
    }

    #createConstraints() {
        for (let i = 0; i <= this.clothSegmentsX; i++) {
            for (let j = 0; j <= this.clothSegmentsZ; j++) {
                const nextI = (i + 1) % (this.clothSegmentsX + 1);
                const nextJ = (j + 1) % (this.clothSegmentsZ + 1);

                const p1 = this.clothParticles[i][j];
                const p2 = this.clothParticles[nextI][j];
                const p3 = this.clothParticles[i][nextJ];
                const p4 = this.clothParticles[nextI][nextJ];

                // add distance constraints
                if (i < this.clothSegmentsX)
                    connect.call(this, p1, p2, this.distance);
                if (j < this.clothSegmentsZ)
                    connect.call(this, p1, p3, this.distance);

                // add bending constraints
                if (i < this.clothSegmentsX - 1 && j < this.clothSegmentsZ - 1) {
                    if ( (i - this.clothSegmentsX * 0.5) * (j - this.clothSegmentsZ * 0.5) < 0 )
                        connect.call(this, p1, p4, this.distance * Math.sqrt(2));
                    else
                        connect.call(this, p2, p3, this.distance * Math.sqrt(2));
                }
            }
        }
        
        function connect(p1, p2, dist) {
            const c = new DistanceConstraint(p1, p2, dist);
            this.world.addConstraint(c);
            this.constraints.push(c);
        }
    }

    disposeCloth() {
        if (!this.animationEnabled)
            settingsData.objects_to_intersection = settingsData.objects_to_intersection.filter(mesh => mesh.userData.type !== SelectedObjectType.Cloth);
        this.clothMesh.geometry.dispose();
        this.clothMesh.material.dispose();
        threeJsVariables.scene.remove(this.clothMesh);
        this.clothParticles.forEach(row => row.forEach(particle => this.world.removeBody(particle)));
        this.constraints.forEach(constraint => this.world.removeConstraint(constraint));
        // dispose this.world cannon
        this.removed = true;
        poiIntersection();
    }

    removeClothIfBelowYZero() {
        if (this.clothParticles.every(row => row.every(particle => particle.position.y < -this.boxSize.y * 0.5))) {
            this.disposeCloth();
        }
    }

    updateClothMesh() {
        const positionAttribute = this.clothGeometry.attributes.position;
        for (let i = 0; i < this.clothParticles.length; i++) {
            for (let j = 0; j < this.clothParticles[i].length; j++) {
                const index = this.clothParticles[i].length * i + j;
                const position = this.clothParticles[i][j].position;
                positionAttribute.setXYZ(index, position.x, position.y, position.z);
            }
        }
        this.clothGeometry.attributes.position.needsUpdate = true;
        
    }

    removeCloth() {
        if (this.removed || !this.active)
            return new Promise(resolve => resolve());
        this.animationEnabled = true;
        settingsData.objects_to_intersection = settingsData.objects_to_intersection.filter(mesh => mesh.userData.type !== SelectedObjectType.Cloth);
        return new Promise(resolve => {
            const interval = setInterval(() => {
                if (this.removed) {
                    clearInterval(interval);
                    resolve();
                }
            }, 200);
        });
    }

    removeClothAnimation() {
        if (!this.animationEnabled) return;
    
        if (this.particleGrab.mass != 0) {
            this.particleGrab.mass = 0;
            this.particleGrab.updateMassProperties();
        }
        this.particleGrab.position.vadd(this.vectorGrab, this.particleGrab.position);
    }

    preSimulate() {
        for (let cycles = 0; cycles < this.speedUpCycles; cycles++) {
            this.world.step(1 / 30);
        }
    }

    animate() {
        this.delta = this.clock.getDelta();
	    this.elapsedTime = this.clock.elapsedTime;

        this.world.step(clamp(this.delta, 1 / 60, 1 / 30));
        this.updateClothMesh();
        this.removeClothAnimation();
        this.removeClothIfBelowYZero();
        updateShadowMap();
        if (!this.removed)
            requestAnimationFrame(this.animate.bind(this));

        function clamp(value, min, max) {
            return Math.min( Math.max(min, value), max );
        }
    }

}
